import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  FileText, CheckCircle2, XCircle, Clock, DollarSign,
  Loader2, Building2, User, Sparkles
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_META = [
  { key: 'hook', label: 'Executive Summary', color: '#8B5CF6' },
  { key: 'pain_points', label: 'Understanding Your Challenges', color: '#EF4444' },
  { key: 'value_proposition', label: 'Our Solution', color: '#10B981' },
  { key: 'scope_of_work', label: 'Scope of Work', color: '#3B82F6' },
  { key: 'deliverables', label: 'Deliverables', color: '#F59E0B' },
  { key: 'pricing', label: 'Investment', color: '#8B5CF6' },
  { key: 'timeline', label: 'Timeline', color: '#06B6D4' },
  { key: 'social_proof', label: 'Why Choose Us', color: '#EC4899' },
  { key: 'call_to_action', label: 'Next Steps', color: '#10B981' },
];

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
}

export default function PublicProposalPage() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responding, setResponding] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [responded, setResponded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/email/proposals/public/${token}`)
      .then(r => { setProposal(r.data); if (r.data.status === 'accepted' || r.data.status === 'rejected') setResponded(true); })
      .catch(err => setError(err.response?.data?.detail || 'Proposal not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleRespond = async (action) => {
    setResponding(true);
    try {
      await axios.post(`${API}/api/email/proposals/public/${token}/respond`, { action, feedback });
      setProposal(p => ({ ...p, status: action }));
      setResponded(true);
      toast.success(action === 'accepted' ? 'Proposal accepted! Thank you.' : 'Proposal declined.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to respond');
    } finally {
      setResponding(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="text-center max-w-sm">
        <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Proposal Not Found</h1>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  const lh = proposal.letterhead || {};
  const accent = lh.accent_color || '#10B981';
  const sections = proposal.sections || {};
  const filledSections = SECTION_META.filter(s => sections[s.key]?.trim());
  const today = proposal.created_at ? new Date(proposal.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-gray-100" data-testid="public-proposal-page">
      <div className="max-w-3xl mx-auto py-6 sm:py-10 px-4">
        {/* Proposal Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Letterhead */}
          <div className="px-6 sm:px-10 py-6 border-b" style={{ borderBottomColor: accent, borderBottomWidth: '3px' }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                {lh.logo_url && <img src={lh.logo_url} alt="Company" className="h-12 max-w-[160px] object-contain mb-2" />}
                {lh.company_name && <p className="text-lg font-bold text-gray-900">{lh.company_name}</p>}
                {lh.company_info && <p className="text-xs text-gray-500 mt-0.5">{lh.company_info}</p>}
              </div>
              <div className="text-left sm:text-right">
                {today && <p className="text-xs text-gray-400">Date: {today}</p>}
                <Badge className={
                  proposal.status === 'accepted' ? 'bg-emerald-100 text-emerald-700 mt-1' :
                  proposal.status === 'rejected' ? 'bg-red-100 text-red-700 mt-1' :
                  proposal.status === 'viewed' ? 'bg-purple-100 text-purple-700 mt-1' :
                  'bg-blue-100 text-blue-700 mt-1'
                }>{proposal.status}</Badge>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="px-6 sm:px-10 py-6 border-b bg-gray-50">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{proposal.title}</h1>
            {proposal.client_name && (
              <p className="text-sm text-gray-600">
                Prepared for: <strong className="text-gray-800">{proposal.client_name}</strong>
                {proposal.client_company && <span className="text-gray-400"> at {proposal.client_company}</span>}
              </p>
            )}
            {proposal.created_by_name && (
              <p className="text-xs text-gray-400 mt-1">By: {proposal.created_by_name}</p>
            )}
          </div>

          {/* Sections */}
          <div className="px-6 sm:px-10 py-6 space-y-8">
            {filledSections.map(({ key, label, color }) => (
              <div key={key}>
                <h2 className="text-base sm:text-lg font-semibold mb-3 pb-2 border-b" style={{ color, borderBottomColor: `${color}33` }}>
                  {label}
                </h2>
                <div
                  className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sections[key] }}
                />
              </div>
            ))}
          </div>

          {/* Total Value */}
          {proposal.total_value > 0 && (
            <div className="mx-6 sm:mx-10 mb-6 p-6 rounded-xl text-center" style={{ background: `linear-gradient(135deg, ${accent}11, ${accent}22)`, border: `1px solid ${accent}33` }}>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Investment</p>
              <p className="text-3xl sm:text-4xl font-extrabold" style={{ color: accent }}>{formatCurrency(proposal.total_value)}</p>
            </div>
          )}

          {/* Response Section */}
          {!responded && (
            <div className="px-6 sm:px-10 py-6 border-t bg-gray-50">
              <h3 className="text-base font-semibold text-gray-800 mb-3">Your Response</h3>
              <div className="mb-4">
                <Label className="text-xs text-gray-500">Feedback (optional)</Label>
                <Textarea
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                  placeholder="Share any thoughts or questions..."
                  rows={3}
                  data-testid="proposal-feedback"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => handleRespond('accepted')}
                  disabled={responding}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-11 text-base gap-2"
                  data-testid="accept-proposal-btn"
                >
                  {responding ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                  Accept Proposal
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleRespond('rejected')}
                  disabled={responding}
                  className="flex-1 h-11 text-base gap-2 text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="reject-proposal-btn"
                >
                  <XCircle className="h-5 w-5" />
                  Decline
                </Button>
              </div>
            </div>
          )}

          {responded && (
            <div className="px-6 sm:px-10 py-6 border-t text-center bg-gray-50">
              {proposal.status === 'accepted' ? (
                <div className="py-3"><CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-2" /><p className="text-lg font-bold text-emerald-700">Proposal Accepted</p><p className="text-sm text-gray-500 mt-1">Thank you! We'll be in touch shortly.</p></div>
              ) : (
                <div className="py-3"><XCircle className="h-12 w-12 text-red-400 mx-auto mb-2" /><p className="text-lg font-bold text-red-600">Proposal Declined</p><p className="text-sm text-gray-500 mt-1">Thank you for your consideration.</p></div>
              )}
            </div>
          )}

          {/* Footer */}
          {lh.footer_text && (
            <div className="px-6 sm:px-10 py-4 border-t text-center">
              <p className="text-xs text-gray-400">{lh.footer_text}</p>
            </div>
          )}
        </div>

        <p className="text-center text-[10px] text-gray-400 mt-4">Powered by Konnect</p>
      </div>
    </div>
  );
}
